import { useCallback, useEffect, useRef, type RefCallback } from 'react';
import useIsMac from './use-is-mac';

export function useKeyDown(
  key: string,
  callback: (event: KeyboardEvent) => void,
  options?: {
    metaKey?: boolean;
    ctrlKey?: boolean;
    altKey?: boolean;
    shiftKey?: boolean;
  }
) {
  const containerRef = useRef<HTMLElement | null>(null);
  const isMac = useIsMac();
  const ref: RefCallback<HTMLElement> = useCallback((ref) => {
    containerRef.current = ref;
  }, []);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (key === e.key) {
        if (
          containerRef.current &&
          !containerRef.current.contains(document.activeElement)
        ) {
          return;
        }

        if (options !== undefined) {
          // Only enforce modifiers that were explicitly provided.
          const matches =
            (options.altKey === undefined || options.altKey === e.altKey) &&
            (options.ctrlKey === undefined || options.ctrlKey === e.ctrlKey) &&
            (options.shiftKey === undefined ||
              options.shiftKey === e.shiftKey) &&
            (options.metaKey === undefined ||
              options.metaKey === (isMac ? e.metaKey : e.ctrlKey));

          if (matches) {
            callback(e);
          }
          return;
        }

        callback(e);
      }
    }

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [callback, isMac, key, options]);

  return ref;
}
